import { apiClient } from "@/lib/axios"
import type {
  CertificadoInstitucional,
  DadosInstitucionais,
  DadosInstitucionaisPayload,
  DadosInstitucionaisPublico,
} from "@/modules/cadastro/types/institucional"

// Dados institucionais (singleton) do sigi-cadastro. O endpoint /publico é
// anônimo (branding de header/login); os demais exigem permissão.
const CADASTRO_ORIGIN = import.meta.env.VITE_CADASTRO_URL ?? ""
const BASE = "/api/v1/institucional"
const OPT = { baseURL: CADASTRO_ORIGIN }

/**
 * URL pública do logotipo (sem autenticação). O parâmetro `versao` funciona
 * como cache-busting a cada troca do arquivo.
 */
export function urlLogoInstitucional(versao?: string | null): string {
  const sufixo = versao ? `?v=${encodeURIComponent(versao)}` : ""
  return `${CADASTRO_ORIGIN}${BASE}/publico/logo${sufixo}`
}

export async function obterInstitucional(): Promise<DadosInstitucionais> {
  const { data } = await apiClient.get<DadosInstitucionais>(BASE, OPT)
  return data
}

export async function atualizarInstitucional(
  payload: DadosInstitucionaisPayload,
): Promise<DadosInstitucionais> {
  const { data } = await apiClient.put<DadosInstitucionais>(BASE, payload, OPT)
  return data
}

export async function enviarLogoInstitucional(arquivo: File): Promise<DadosInstitucionais> {
  const form = new FormData()
  form.append("arquivo", arquivo)
  const { data } = await apiClient.post<DadosInstitucionais>(`${BASE}/logo`, form, {
    ...OPT,
    headers: { "Content-Type": "multipart/form-data" },
  })
  return data
}

export async function removerLogoInstitucional(): Promise<void> {
  await apiClient.delete(`${BASE}/logo`, OPT)
}

export async function enviarCertificadoInstitucional(
  arquivo: File,
  senha: string,
): Promise<CertificadoInstitucional> {
  const form = new FormData()
  form.append("arquivo", arquivo)
  form.append("senha", senha)
  const { data } = await apiClient.post<CertificadoInstitucional>(`${BASE}/certificado`, form, {
    ...OPT,
    headers: { "Content-Type": "multipart/form-data" },
  })
  return data
}

export async function removerCertificadoInstitucional(): Promise<void> {
  await apiClient.delete(`${BASE}/certificado`, OPT)
}

/**
 * Branding público — usa `fetch` puro para não passar pelo interceptor de
 * autenticação (a tela de login roda sem sessão).
 */
export async function obterInstitucionalPublico(): Promise<DadosInstitucionaisPublico> {
  const resposta = await fetch(`${CADASTRO_ORIGIN}${BASE}/publico`, {
    headers: { Accept: "application/json" },
  })
  if (!resposta.ok) {
    throw new Error("Não foi possível carregar os dados institucionais.")
  }
  return (await resposta.json()) as DadosInstitucionaisPublico
}

/**
 * URL pública do logotipo quando houver um cadastrado. Falhas de rede ou
 * cadastro indisponível não impedem a emissão de fichas (retorna `null`).
 */
export async function obterUrlLogoInstitucional(): Promise<string | null> {
  try {
    const dados = await obterInstitucionalPublico()
    return dados.temLogo ? urlLogoInstitucional(dados.atualizadoEm) : null
  } catch {
    return null
  }
}
